import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { showError } from "../utils/toast";
import { colors } from "../theme/modernTheme";
import { useDispatch } from "react-redux";
import { createTag, updateTag } from "../store/slices/tagsSlice";

export default function TagDialog({
  open,
  onClose,
  tag,
  onSuccess,
  initialName = "",
}) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#5A6ACF",
  });
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      if (tag) {
        setFormData({
          name: tag.name || "",
          description: tag.description || "",
          color: tag.color || "#5A6ACF",
        });
      } else {
        setFormData({
          name: initialName || "",
          description: "",
          color: "#5A6ACF",
        });
      }
    }
  }, [tag, open, initialName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});

    try {
      let resultAction;
      if (tag) {
        resultAction = await dispatch(
          updateTag({ id: tag.id, data: formData })
        );
      } else {
        resultAction = await dispatch(createTag(formData));
      }

      if (
        createTag.fulfilled.match(resultAction) ||
        updateTag.fulfilled.match(resultAction)
      ) {
        // Close dialog first
        onClose();
        // Small delay to ensure dialog closes
        setTimeout(() => {
          if (onSuccess) onSuccess(resultAction.payload);
        }, 100);
      } else {
        throw new Error(resultAction.payload || "Failed to save tag");
      }
    } catch (err) {
      console.error("[TagDialog] Error creating/updating tag:", err);
      if (err.error?.code === "VALIDATION_ERROR") {
        const errors = err.error.details?.field_errors || {};
        setFieldErrors(errors);
        // Don't show toast - field errors are displayed below each field
      } else {
        showError(err?.message || "Failed to save tag");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "4px",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        },
      }}>
      <form onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: `1px solid ${colors.border.main}`,
          }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: colors.text.primary,
              fontSize: "1.1rem",
            }}>
            {tag ? "Edit Tag" : "Create New Tag"}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 4 }}>
            <TextField
              fullWidth
              label="Tag Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              autoFocus
              error={!!fieldErrors.name}
              helperText={fieldErrors.name || `${formData.name.length}/20`}
              inputProps={{
                maxLength: 20,
              }}
              FormHelperTextProps={{
                sx: { marginLeft: 0, marginRight: 0, textAlign: fieldErrors.name ? "left" : "right" },
              }}
            />

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              error={!!fieldErrors.description}
              helperText={fieldErrors.description || `${formData.description.length}/1000`}
              inputProps={{
                maxLength: 1000,
              }}
              FormHelperTextProps={{
                sx: { marginLeft: 0, marginRight: 0, textAlign: fieldErrors.description ? "left" : "right" },
              }}
            />

            <Box>
              <Typography
                variant="caption"
                sx={{ color: colors.text.secondary, display: "block", mb: 1 }}>
                Tag Color
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <input
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  style={{
                    width: "60px",
                    height: "40px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                />
                <TextField
                  size="small"
                  value={formData.color}
                  onChange={handleChange}
                  name="color"
                  sx={{ width: "120px" }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            py: 2,
            px: 1,
            borderTop: `1px solid ${colors.border.main}`,
            gap: 1,
          }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderColor: colors.error.main,
              color: colors.error.main,
              borderRadius: "4px",
              "&:hover": {
                borderColor: colors.error.dark,
                backgroundColor: "rgba(220, 38, 38, 0.04)",
              },
            }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              backgroundColor: colors.primary.main,
              "&:hover": {
                backgroundColor: colors.primary.dark,
              },
            }}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
